import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import {
  Image,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Building2,
  Sparkles,
  Clock,
  Camera,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface GenerationStatus {
  personas: {
    total: number;
    with_images: number;
    missing_images: number;
  };
  sectors: {
    total: number;
    with_images: number;
    missing_images: number;
  };
}

interface GenerationResult {
  personaId?: string;
  sectorId?: string;
  productId?: string;
  code?: string;
  superseded?: boolean;
  repairedNow?: boolean;
  conflict?: boolean;
  claimCleanupFailed?: boolean;
  persistError?: string;
  name: string;
  success: boolean;
  skipped?: boolean;
  reason?: string;
  imageUrl?: string;
  error?: string;
}

/**
 * One place that knows how each generation kind is named. Adding a fourth kind means adding a row
 * here; TypeScript then requires it, rather than a ternary quietly mislabelling it at runtime.
 */
const GENERATION_LABELS: Record<'personas' | 'sectors' | 'products', {
  noun: string;
  plural: string;
  /**
   * ⛔ WHAT ACTUALLY GETS BILLED ON A NON-REGENERATE RUN, per kind — NOT a friendly approximation.
   *   This was one shared sentence, "…that don't have images yet", and for PRODUCTS that is simply
   *   false: the endpoint skips only products already carrying one of OUR GENERATED images, so a
   *   product with a perfectly good VENDOR photograph has no generated image and IS BILLED. The copy
   *   promised a smaller cohort than the run buys, and it contradicted the accurate warning sitting
   *   a few lines below it.
   * ⇒ Keyed per kind, like the headings and the labels, so each one states its own real skip rule
   *   and a fourth kind must declare its own rather than inheriting a sentence that may not be true.
   */
  missingOnly: string;
}> = {
  personas: { noun: 'Persona', plural: 'personas', missingOnly: "personas that don't have images yet" },
  sectors: { noun: 'Sector', plural: 'sectors', missingOnly: "sectors that don't have images yet" },
  products: {
    noun: 'Product', plural: 'products',
    missingOnly: "products that don't already have one of OUR generated images — a product with only "
               + 'a vendor image WILL be generated and billed',
  },
};

interface Site {
  id: string;
  name: string;
  url: string;
}

/**
 * The products endpoint answers in its OWN shape — it does not return `stats` like the persona and
 * sector endpoints, and it distinguishes outcomes those two have no concept of. Mapping it onto the
 * older shape would have silently dropped exactly the fields that say a run did NOT do what was
 * asked, which is the whole point of that endpoint's outcome taxonomy.
 */
/** The endpoint's refusal shapes, as it actually returns them. */
/**
 * ⛔⛔ HEADINGS KEYED BY CODE, AND THE DEFAULT IS THE CONSERVATIVE ONE.
 *   This was a two-way ternary: migration_required got its own heading and EVERYTHING ELSE got
 *   "Request refused — nothing was generated". Then I added request_failed for a lost response —
 *   whose body correctly says images MAY have been generated — and it inherited a heading asserting
 *   the opposite. The screen contradicted itself on a billable operation, and the heading is what an
 *   operator acts on: read "nothing was generated", retry with regenerate, buy every image twice.
 * 🔑 Same class as the "Sector Images" label and the failure buckets, in the same file, ONE LINE
 *   ABOVE the body I had just fixed for exactly this reason. I added a member and did not re-read
 *   what classifies it.
 * ⇒ Only codes that PROVABLY refused before any spend may claim nothing was generated. Anything
 *   unrecognised — including whatever the endpoint returns next — falls to "outcome unknown", which
 *   is the honest answer when we do not know. The default must be the safe claim, not the tidy one.
 */
const REFUSAL_HEADINGS: Record<string, string> = {
  migration_required: 'This environment is not ready',
  request_failed: 'Generation status unknown — images may have been created',
  PRODUCT_IDS_UNRESOLVED: 'Request refused — nothing was generated',
  invalid_request: 'Request refused — nothing was generated',
};
const UNKNOWN_OUTCOME_HEADING = 'Request outcome unknown';

interface ProductRequestError {
  error: string;
  code?: string;
  /** PRODUCT_IDS_UNRESOLVED carries the offending ids — showing the count without them is useless. */
  unresolved?: string[];
  resolved?: number;
}

/**
 * ⛔⛔ ONE REGISTRY FOR EVERY FAILURE BUCKET, AND A CATCH-ALL FOR THE ONES NOBODY REGISTERED.
 *
 *   Hand-listing which buckets to render has now failed THREE ROUNDS RUNNING in this file. Each
 *   round I added the ones the review named, and the next review named a different one —
 *   generationFailures, then claimConflicts and claimCleanupFailures, then persistEscapes. That is
 *   not carelessness repeated three times; it is the predictable behaviour of a hand-list against a
 *   set that grows. The counts always said something failed while the rows naming the affected
 *   product were absent.
 *
 * ⇒ TWO MECHANISMS, because the registry alone would just be a nicer hand-list:
 *   1. Registered buckets render with DETAIL — the product ids and their error or code.
 *   2. Anything in `totals` that means failure and is NOT registered still renders, generically,
 *      from the totals key itself. So a bucket the endpoint adds tomorrow surfaces on its own; the
 *      worst case is a less pretty message, never silence.
 *
 * 🔑 That inversion is the point. Before, a bucket appeared only if I remembered it. Now it appears
 *   unless someone deliberately classifies it as benign, and forgetting produces a visible generic
 *   warning rather than a green screen. Same cure as the single exit for the claim paths and the
 *   keyed label map — make the guarantee structural instead of remembered.
 */

/** Totals keys that describe WORK DONE. Everything else in totals is a failure of some kind. */
const BENIGN_TOTAL_KEYS = new Set([
  'products', 'eligible', 'generated', 'skipped', 'repaired', 'written',
]);

type BucketRow = { productId?: string; error?: string; reason?: string; code?: string; persistError?: string };

const FAILURE_BUCKETS: Array<{
  /** MUST match the key in `totals`, so registered and unregistered buckets cannot double-report. */
  key: string;
  title: string;
  /** Where the detail rows come from: an array the endpoint sends, or a predicate over results. */
  rows: (summary: ProductRunSummary, results: GenerationResult[]) => BucketRow[];
}> = [
  { key: 'incompleteOutcomes', title: 'Could not be completed',
    rows: (s) => s.incompleteOutcomes || [] },
  { key: 'preflightFailures', title: 'Not attempted',
    rows: (s) => s.preflightFailures || [] },
  { key: 'writeFailures', title: 'Generated but not recorded',
    rows: (s) => s.writeFailures || [] },
  { key: 'generationFailures', title: 'Generation failed',
    rows: (_s, r) => r.filter((x) => !x.success && !x.skipped) },
  { key: 'claimConflicts', title: 'Another run already held the claim (retrying is safe)',
    rows: (_s, r) => r.filter((x) => !!x.conflict) },
  { key: 'claimCleanupFailures', title: 'Advisory lock was NOT released — this connection is poisoned',
    rows: (_s, r) => r.filter((x) => !!x.claimCleanupFailed) },
  { key: 'persistEscapes', title: 'Image generated but not persisted',
    rows: (_s, r) => r.filter((x) => !!x.persistError) },
];

interface ProductRunSummary {
  success: boolean;
  /**
   * ⚠️ INDEXED, DELIBERATELY. A closed literal type would make a totals key the endpoint adds later
   *   unreachable in the catch-all below — the type would be "correct" and the bucket still silent.
   */
  totals: Record<string, number> & {
    products: number;
    eligible: number;
    generated: number;
    skipped: number;
    repaired: number;
    written: number;
  };
  incompleteOutcomes?: Array<{ productId?: string; code?: string; reason?: string }>;
  integrityGaps?: Array<{ productId?: string; duplicateSourceIdEntries?: number }>;
  preflightFailures?: Array<{ productId?: string; error?: string }>;
  writeFailures?: Array<{ productId?: string; error?: string; code?: string }>;
}

export default function ImageGeneration() {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  /** NOT site-scoped: this is about loading the site LIST, so it must outlive any selection. */
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [generationType, setGenerationType] = useState<'personas' | 'sectors' | 'products' | null>(null);

  /**
   * ⛔⛔ EVERYTHING THAT BELONGS TO THE SELECTED SITE IS ONE OBJECT, RESET AS A UNIT.
   *   The previous version cleared results / summary / error / productIds on a site change and NOT
   *   regenerateAll — so a "regenerate" ticked on site A carried into site B and turned a
   *   missing-only run into a PAID REPLACEMENT. Adding regenerateAll to the clear-list would have
   *   fixed that instance and forgotten the next site-owned control, which is the exact pattern
   *   that cost this file four rounds.
   * ⇒ A new site-owned field goes in SITE_SCOPED_DEFAULTS and is therefore cleared BY CONSTRUCTION.
   *   There is no clear-list to forget.
   * ⚠️ `regenerateAll` and `results` are shared with the persona and sector tabs deliberately — they
   *   are site-owned too, and a stale "regenerate all" surviving a site switch is exactly as
   *   expensive there.
   */
  const SITE_SCOPED_DEFAULTS = {
    /**
     * Smoke-one-before-a-batch as an AFFORDANCE, not a convention somebody remembers: every image is
     * a real charge, and an empty field commits to the whole catalogue in one click.
     */
    productIds: '',
    regenerateAll: false,
    results: [] as GenerationResult[],
    productSummary: null as ProductRunSummary | null,
    /** Refusals need a persistent home — a toast takes the failing IDs away with it. */
    productError: null as ProductRequestError | null,
    /**
     * ⛔ status IS SITE-OWNED TOO, and leaving it out of this object was the same omission one more
     *   time (codex-reviewer-2 :261). Its persona/sector counts belong to a site; surviving a switch
     *   they showed site A's totals under site B and ENABLED ACTIONS against them. The unit exists
     *   precisely so a site-owned field cannot be forgotten — so it has to actually contain them.
     */
    status: null as GenerationStatus | null,
    /** Site-owned like everything else here — see the note above; it goes IN the unit, not beside it. */
    statusError: null as string | null,
  };
  type SiteScoped = typeof SITE_SCOPED_DEFAULTS;

  const [siteScoped, setSiteScoped] = useState<SiteScoped>(SITE_SCOPED_DEFAULTS);
  const { productIds, regenerateAll, results, productSummary, productError, status, statusError } = siteScoped;
  const patchSite = (patch: Partial<SiteScoped>) => setSiteScoped((prev) => ({ ...prev, ...patch }));

  /**
   * Parsed once so the confirmation dialog and the request can never disagree about the cohort.
   * ⚠️ Must sit BELOW the destructure above — it reads productIds, and declaring it earlier is a
   *   temporal-dead-zone error, which is how this file failed to compile a moment ago.
   */
  const scopedProductIds = productIds.split(',').map((v) => v.trim()).filter(Boolean);

  /**
   * ⛔ A RESPONSE BELONGS TO THE SITE IT WAS REQUESTED FOR. The state reset handles the synchronous
   *   switch; it does nothing about a request already in flight, whose late setState paints site A's
   *   outcome under site B. A ref is required rather than the state value because the async closure
   *   captures selectedSiteId as it was when the request started.
   */
  const currentSiteRef = useRef(selectedSiteId);
  const [progress, setProgress] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSites();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    /**
     * ⛔ A RUN BELONGS TO THE SITE IT RAN AGAINST. Switching sites refetched status but left the
     *   previous site's results, summary, error and typed product IDs on screen — so site B could
     *   display site A's product IDs and outcome, on the one screen whose purpose is confirming a
     *   PER-SITE spend. An operator checking "did GOLD-0001 generate?" would be reading another
     *   site's answer, and the IDs would still be in the field when they pressed generate.
     */
    // Reset the WHOLE site-scoped unit — no field can be forgotten because there is no list.
    setSiteScoped(SITE_SCOPED_DEFAULTS);
    currentSiteRef.current = selectedSiteId;
    if (selectedSiteId) {
      fetchStatus();
    }
  }, [selectedSiteId]);

  /**
   * ⛔ A FAILED RELOAD MUST DROP THE OLD LIST **AND** THE OLD SELECTION.
   *   Every failure path used to `return` early, which left the previously-loaded sites and
   *   selectedSiteId intact — so a later refresh could show "could not load sites" while a site from
   *   the PREVIOUS load stayed selected and every generation action stayed enabled. The operator
   *   would then be spending against a selection the page just admitted it cannot verify.
   * 🔑 Same class as the site-switch work: clearing one half of a coupled state and leaving the
   *   other is how a guard produces a state that is internally inconsistent rather than merely
   *   stale. The selection is meaningless without the list it came from, so they fail together.
   */
  const failSiteList = (message: string) => {
    setSitesError(message);
    setSites([]);
    setSelectedSiteId('');
  };

  const fetchSites = async () => {
    /**
     * ⛔⛔ THIS PAGE HAS NEVER BEEN USABLE, AND `|| []` IS WHY NOBODY KNEW.
     *   GET /api/sites responds with a BARE ARRAY (routes/sites.js: `res.json(result.rows)`, no
     *   filtering of any kind). This read `data.sites`, which on an array is `undefined`, and the
     *   `|| []` turned that into an empty list — so the Select Site dropdown was EMPTY, with NO
     *   console error, and since a site must be chosen before ANY tab operates, the whole page was
     *   dead. That includes the ORIGINAL persona and sector generation, not just the products tab:
     *   fetchSites is byte-identical to before the products work, so this predates it.
     *   (The only `{ sites: [...] }` envelope in the backend is routes/data-management.js — a
     *   different endpoint. The shape was wrong from the start.)
     * 🔑 A silent fallback turned "I cannot read this response" into "there are no sites", and the
     *   two are indistinguishable on screen. That is the whole argument against `|| []` on a value
     *   you did not verify: it does not prevent the failure, it hides it.
     * ⇒ Accept either shape, and when neither is recognisable SAY SO instead of rendering an empty
     *   dropdown that looks like an empty account.
     */
    try {
      const response = await authFetch('/api/sites');
      if (!response.ok) {
        failSiteList(`Could not load sites (${response.status}).`);
        return;
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.sites) ? data.sites
        : null;
      if (!list) {
        console.error('Unexpected /api/sites response shape:', data);
        failSiteList('The site list could not be read — the server returned an unexpected shape.');
        return;
      }
      setSitesError(null);
      setSites(list);
      /**
       * ⛔ ALWAYS SET THE SELECTION FROM THE LIST WE JUST RECEIVED, INCLUDING WHEN IT IS EMPTY.
       *   The guarded form (`if (list.length > 0)`) left a previously-selected site in place when a
       *   SUCCESSFUL response came back empty — so the page could show "no sites" while a site
       *   stayed selected and the billable generate button stayed live against a list that no longer
       *   contains it. failSiteList() covers the FAILURE paths; this is the same hazard on the
       *   SUCCESS path, which is why it needs its own line rather than being folded into that.
       * 🔑 Unconditional is also why this needs no reachability argument: the selection is derived
       *   from the response every time, so it cannot disagree with the list it came from.
       */
      setSelectedSiteId(list[0]?.id ?? '');
    } catch (error) {
      console.error('Error fetching sites:', error);
      failSiteList('The site list could not be loaded — the request failed.');
    }
  };

  const fetchStatus = async () => {
    if (!selectedSiteId) return;

    /**
     * Same rule as the generation call: a response belongs to the site it was requested for. Without
     * this, a slow status fetch for site A lands after a switch and repopulates site B's panel with
     * A's counts — the very state the reset just cleared.
     */
    const requestSiteId = selectedSiteId;
    try {
      const response = await authFetch(`/api/image-generation/status/${requestSiteId}`);
      if (currentSiteRef.current !== requestSiteId) return;
      if (response.ok) {
        const data = await response.json();
        patchSite({ status: data, statusError: null });
      } else {
        /**
         * ⛔ A NON-OK STATUS RESPONSE WAS SILENTLY DISCARDED — no state, no message, just an absent
         *   panel. Combined with the tab set being gated on `status`, that meant a hiccup in the
         *   persona/sector counts endpoint made the whole screen look empty rather than broken.
         */
        patchSite({ statusError: `Could not load persona/sector counts (${response.status}).` });
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      if (currentSiteRef.current !== requestSiteId) return;
      patchSite({ statusError: 'Could not load persona/sector counts — the request failed.' });
    }
  };

  const handleGenerate = async (type: 'personas' | 'sectors' | 'products') => {
    setGenerationType(type);
    setConfirmDialog(true);
  };

  const confirmGeneration = async () => {
    setConfirmDialog(false);
    if (!generationType) return;

    setGenerating(true);
    patchSite({ results: [], productSummary: null });
    setProgress(0);

    /**
     * ⛔ THE PRODUCTS ENDPOINT IS HANDLED SEPARATELY, DELIBERATELY. It answers with `totals` and an
     *   outcome taxonomy rather than `stats`, and it can report a run that succeeded at every write
     *   and still did NOT do what was asked — a product that left the catalogue, a knowledge-base
     *   linkage that changed, a divergence it could not repair. Folding it into the branch below
     *   would read `data.stats.total` as undefined and show the operator a cheerful NaN, and worse,
     *   it would discard those outcomes entirely: the response deliberately carries them so a human
     *   sees them, and a UI that drops them repeats the mistake the endpoint was fixed to stop.
     */
    if (generationType === 'products') {
      patchSite({ productError: null });
      /**
       * ⛔⛔ THE SITE THIS REQUEST IS FOR, CAPTURED BEFORE IT LEAVES. Clearing state on a site change
       *   handles the SYNCHRONOUS switch and does nothing about a request already in flight: its
       *   late setState paints site A's outcome under site B, on the screen whose whole purpose is
       *   confirming a per-site spend. Every write below is gated on this still being the selected
       *   site — a response belongs to the site it was requested for.
       */
      const requestSiteId = selectedSiteId;
      const stillCurrent = () => currentSiteRef.current === requestSiteId;
      try {
        const ids = scopedProductIds;
        const response = await authFetch('/api/image-generation/products', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Only send productIds when the operator named some — an empty array means
          // "generate nothing", which is a different request from "the whole site".
          body: JSON.stringify({ siteId: selectedSiteId, regenerateAll, ...(ids.length ? { productIds: ids } : {}) })
        });
        const data = await response.json();
        // The operator moved on; this answer is about a site they are no longer looking at.
        if (!stillCurrent()) return;

        if (!response.ok) {
          /**
           * ⚠️ THE BODY IS KEPT, NOT JUST THE MESSAGE. PRODUCT_IDS_UNRESOLVED carries `unresolved`
           *   -- the exact ids that failed -- and a 503 carries code 'migration_required', which
           *   tells an operator the deploy is incomplete rather than that they did something wrong.
           *   Reducing either to a toast throws away the part that says what to DO.
           */
          patchSite({ productError: {
            error: data.error || `Generation failed (${response.status})`,
            code: data.code,
            unresolved: data.unresolved,
            resolved: data.resolved,
          } });
          toast.error(data.error || `Generation failed (${response.status})`);
          patchSite({ productSummary: data && data.totals ? data : null });
          return;
        }

        patchSite({ results: data.results || [] });
        patchSite({ productSummary: data });
        const t = data.totals || {};
        const done = (t.generated || 0) + (t.skipped || 0) + (t.repaired || 0);
        setProgress(t.products ? (done / t.products) * 100 : 0);

        /**
         * ⚠️ THE TOAST FOLLOWS `success`, NOT THE HTTP STATUS. A 200 here can still be an
         *   unsuccessful run — that distinction is the endpoint's contract and the operator is the
         *   person it exists for.
         */
        if (data.success) {
          toast.success(`Generated ${t.generated || 0}, repaired ${t.repaired || 0}, skipped ${t.skipped || 0}`);
        } else {
          toast.error(
            `Run incomplete — ${t.incompleteOutcomes || 0} unfinished, ${t.writeFailures || 0} write failures, `
            + `${t.generationFailures || 0} generation failures, ${t.claimCleanupFailures || 0} unreleased locks. `
            + 'See the breakdown below.'
          );
        }
      } catch (error) {
        /**
         * ⛔ THE CATCH GETS THE PERSISTENT PANEL TOO — otherwise it reproduces the exact pattern the
         *   panel was added to remove. A dropped connection, a proxy returning HTML, or a body that
         *   is not JSON all land here, and a toast tells the operator "failed" for three seconds and
         *   then leaves them with a screen that looks like nothing happened.
         * ⚠️ AND WHETHER MONEY WAS SPENT IS GENUINELY UNKNOWN HERE: the request may have reached the
         *   endpoint and generated images before the response was lost. Saying so is the honest
         *   message — telling them to re-run blindly could buy every image twice, and the endpoint's
         *   own skip logic is what makes a re-run safe rather than a guess.
         */
        console.error('Error generating product images:', error);
        if (!stillCurrent()) return;
        patchSite({ productError: {
          error: 'The request did not complete — the response was lost or unreadable. Images may or '
               + 'may not have been generated. Re-running is safe: products that already have one of '
               + 'our images are skipped unless you tick regenerate.',
          code: 'request_failed',
        } });
        toast.error('Failed to generate product images');
      } finally {
        setGenerating(false);
        setGenerationType(null);
      }
      return;
    }

    try {
      const endpoint = generationType === 'personas'
        ? '/api/image-generation/personas'
        : '/api/image-generation/sectors';

      const body = generationType === 'personas'
        ? { siteId: selectedSiteId, regenerateAll }
        : { regenerateAll };

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        patchSite({ results: data.results });

        // Calculate progress
        const total = data.stats.total;
        const completed = data.stats.successful + data.stats.skipped + data.stats.failed;
        setProgress((completed / total) * 100);

        // Show success message
        toast.success(
          `Generated ${data.stats.successful} images, skipped ${data.stats.skipped}, failed ${data.stats.failed}`
        );

        // Refresh status
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Error generating images:', error);
      toast.error('Failed to generate images');
    } finally {
      setGenerating(false);
      setGenerationType(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This page is only accessible to super administrators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">AI Image Generation</h1>
        <p className="text-muted-foreground">
          Generate photo-realistic images for buyer personas and business sectors using Leonardo.ai
        </p>
      </div>

      {/* Site Selector */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Site</CardTitle>
          <CardDescription>
            Choose a site to manage its persona and sector images
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/*
            ⛔ SHOWN, NOT JUST RECORDED. The failure this replaces was invisible precisely because
               nothing rendered it — an empty dropdown reads as "no sites exist", which is a
               different and much less alarming statement than "I could not read the site list".
          */}
          {sitesError && (
            <Alert className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {sitesError}
                <Button variant="outline" size="sm" className="ml-3" onClick={fetchSites}>Retry</Button>
              </AlertDescription>
            </Alert>
          )}
          {!sitesError && sites.length === 0 && (
            <Alert className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No sites were returned. This is a real empty list, not a load failure.
              </AlertDescription>
            </Alert>
          )}
          <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map(site => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name} - {site.url}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/*
        ⛔⛔ THE TABS ARE GATED ON A SITE BEING SELECTED, NOT ON `status`.
          Every tab — including Product Images, which does not use persona or sector counts at all —
          used to be hidden unless the LEGACY status endpoint returned. So a transient failure in an
          unrelated endpoint made a healthy, billable feature VANISH with no error on screen: the
          operator sees an empty page and cannot tell "not available here" from "broken right now".
        🔑 My own status-guard made this worse, not better: clearing `status` on a site change meant
          the tabs disappeared during every fetch and stayed gone if it failed. A fix that narrows
          one staleness bug and widens a silent-failure one is not an improvement.
        ⇒ A dependency failing should take down ITSELF, visibly — not everything mounted beside it.
      */}
      {selectedSiteId && (
        <Tabs defaultValue="products" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="personas">
              <Users className="h-4 w-4 mr-2" />
              Buyer Personas
            </TabsTrigger>
            <TabsTrigger value="sectors">
              <Building2 className="h-4 w-4 mr-2" />
              Business Sectors
            </TabsTrigger>
            <TabsTrigger value="products">
              <Camera className="h-4 w-4 mr-2" />
              Product Images
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Product Images
                  </span>
                </CardTitle>
                <CardDescription>
                  Generates a product photograph and stores it in its own fields, so a feed import
                  cannot overwrite it. The vendor&apos;s own <code>image_url</code> is left untouched.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="productIds">
                    Product IDs <span className="text-muted-foreground">(comma separated)</span>
                  </label>
                  <input
                    id="productIds"
                    type="text"
                    value={productIds}
                    onChange={(e) => patchSite({ productIds: e.target.value })}
                    placeholder="GOLD-0001"
                    disabled={generating}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                  {/*
                    Every image is a real charge. Naming one product first bounds a mistake to one
                    image; leaving this empty commits to the whole catalogue in a single click.
                  */}
                  <p className="text-xs text-muted-foreground">
                    Leave empty to cover the whole site. Generating one product first is the cheap
                    way to check the result before committing to a batch — each image is billed.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="regenerateAllProducts"
                    type="checkbox"
                    checked={regenerateAll}
                    onChange={(e) => patchSite({ regenerateAll: e.target.checked })}
                    disabled={generating}
                  />
                  <label htmlFor="regenerateAllProducts" className="text-sm">
                    Regenerate products that already have one of our images (buys a new image)
                  </label>
                </div>

                <Button
                  onClick={() => handleGenerate('products')}
                  disabled={generating || !selectedSiteId}
                >
                  {generating && generationType === 'products' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Product Images
                    </>
                  )}
                </Button>

                {generating && generationType === 'products' && (
                  <Progress value={progress} />
                )}
              </CardContent>
            </Card>

            {productError && (
              <Alert>
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{REFUSAL_HEADINGS[productError.code || ''] || UNKNOWN_OUTCOME_HEADING}</strong>
                  <p className="mt-1 text-sm">{productError.error}</p>
                  {productError.unresolved && productError.unresolved.length > 0 && (
                    <>
                      {/*
                        The whole request is refused rather than generating for the subset that did
                        resolve, so naming the failures is what makes the refusal actionable.
                      */}
                      <p className="mt-2 text-xs font-medium">These product IDs did not resolve:</p>
                      <ul className="mt-1 list-disc pl-5 text-xs">
                        {productError.unresolved.map((id) => (
                          <li key={id}><code>{id}</code></li>
                        ))}
                      </ul>
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {productSummary && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {productSummary.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    {productSummary.success ? 'Run complete' : 'Run incomplete'}
                  </CardTitle>
                  <CardDescription>
                    {/*
                      ⛔ A COUNT NAMES A PROBLEM WITHOUT NAMING THE PRODUCT. Every non-zero bucket
                         below is expanded into rows, because the operator has to know WHICH one.
                    */}
                    {productSummary.totals.generated} generated · {productSummary.totals.repaired} repaired
                    {' · '}{productSummary.totals.skipped} skipped · {productSummary.totals.products} considered
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/*
                    integrityGaps is NOT a failure bucket — the run succeeded; this is a data
                    condition a human has to act on, so it keeps its own bespoke wording.
                  */}
                  {(productSummary.integrityGaps || []).length > 0 && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Data integrity:</strong> {productSummary.integrityGaps!.length} product(s) have
                        more than one knowledge-base entry sharing a source id, so the generated image
                        cannot be resolved unambiguously.
                        <ul className="mt-1 list-disc pl-5 text-xs">
                          {productSummary.integrityGaps!.map((g, i) => (
                            <li key={i}><code>{g.productId}</code> — {g.duplicateSourceIdEntries} entries</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {FAILURE_BUCKETS.map((bucket) => {
                    const rows = bucket.rows(productSummary, results);
                    if (rows.length === 0) return null;
                    return (
                      <Alert key={bucket.key}>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>{bucket.title} ({rows.length})</strong>
                          <ul className="mt-1 list-disc pl-5 text-xs">
                            {rows.map((r, i) => (
                              <li key={i}>
                                <code>{r.productId}</code>
                                {(r.error || r.persistError || r.reason) && <> — {r.error || r.persistError || r.reason}</>}
                                {r.code && <> <span className="text-muted-foreground">({r.code})</span></>}
                              </li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    );
                  })}

                  {/*
                    ⛔⛔ THE CATCH-ALL. Any non-zero totals key that is neither work-done nor a
                       registered bucket renders here. This is what makes "surface every failure"
                       structural: a bucket the endpoint adds tomorrow cannot be silently dropped by
                       a UI that has not heard of it — the worst case is this plainer message.
                  */}
                  {Object.entries(productSummary.totals)
                    .filter(([k, v]) => v > 0
                      && !BENIGN_TOTAL_KEYS.has(k)
                      && !FAILURE_BUCKETS.some((b) => b.key === k))
                    .map(([k, v]) => (
                      <Alert key={k}>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>{v} product(s) reported under &ldquo;{k}&rdquo;.</strong>{' '}
                          This run reported a failure category this screen does not have a detailed
                          view for yet — check the server logs for the affected products.
                        </AlertDescription>
                      </Alert>
                    ))}

                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : result.skipped ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="flex-1">
                          <code className="text-xs">{result.productId}</code>{' '}
                          {result.name}
                        </span>
                        {/*
                          ⚠️ A CLEANUP FAILURE CAN RIDE ON A SUCCESSFUL GENERATION, so the row-level
                             success tick is not the whole story and must not be shown alone.
                        */}
                        {result.claimCleanupFailed && (
                          <span className="text-xs font-medium text-red-500">lock not released</span>
                        )}
                        {result.imageUrl && (
                          <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            View Image
                          </a>
                        )}
                        {(result.error || result.persistError) && (
                          <span className="text-xs text-red-500">{result.error || result.persistError}</span>
                        )}
                        {result.reason && (
                          <span className="text-xs text-muted-foreground">{result.reason}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Personas Tab */}
          <TabsContent value="personas" className="space-y-4">
            {/* These panels genuinely need the counts, so they explain themselves when absent. */}
            {!status && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {statusError || 'Loading counts…'}
                  {statusError && (
                    <Button variant="outline" size="sm" className="ml-3" onClick={fetchStatus}>
                      Retry
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {status && (<Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Buyer Personas
                  </span>
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {status.personas.with_images} / {status.personas.total}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Generate photo-realistic images for buyer personas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{status.personas.total}</div>
                    <div className="text-sm text-muted-foreground">Total Personas</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {status.personas.with_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Have Images</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {status.personas.missing_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Missing Images</div>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <Button
                    onClick={() => {
                      patchSite({ regenerateAll: false });
                      handleGenerate('personas');
                    }}
                    disabled={generating || status.personas.missing_images === 0}
                    variant="default"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Generate Missing ({status.personas.missing_images})
                  </Button>

                  <Button
                    onClick={() => {
                      patchSite({ regenerateAll: true });
                      handleGenerate('personas');
                    }}
                    disabled={generating}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate All ({status.personas.total})
                  </Button>
                </div>

                {generating && generationType === 'personas' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Generating images...</span>
                      <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}
              </CardContent>
            </Card>)}

            {/* Results for Personas */}
            {results.length > 0 && generationType === 'personas' && (
              <Card>
                <CardHeader>
                  <CardTitle>Generation Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : result.skipped ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="flex-1">{result.name}</span>
                        {result.imageUrl && (
                          <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            View Image
                          </a>
                        )}
                        {result.reason && (
                          <span className="text-xs text-muted-foreground">{result.reason}</span>
                        )}
                        {result.error && (
                          <span className="text-xs text-red-500">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Sectors Tab */}
          <TabsContent value="sectors" className="space-y-4">
            {/* These panels genuinely need the counts, so they explain themselves when absent. */}
            {!status && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {statusError || 'Loading counts…'}
                  {statusError && (
                    <Button variant="outline" size="sm" className="ml-3" onClick={fetchStatus}>
                      Retry
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {status && (<Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Business Sectors
                  </span>
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {status.sectors.with_images} / {status.sectors.total}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Generate professional images for business sectors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{status.sectors.total}</div>
                    <div className="text-sm text-muted-foreground">Total Sectors</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {status.sectors.with_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Have Images</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {status.sectors.missing_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Missing Images</div>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <Button
                    onClick={() => {
                      patchSite({ regenerateAll: false });
                      handleGenerate('sectors');
                    }}
                    disabled={generating || status.sectors.missing_images === 0}
                    variant="default"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Generate Missing ({status.sectors.missing_images})
                  </Button>

                  <Button
                    onClick={() => {
                      patchSite({ regenerateAll: true });
                      handleGenerate('sectors');
                    }}
                    disabled={generating}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate All ({status.sectors.total})
                  </Button>
                </div>

                {generating && generationType === 'sectors' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Generating images...</span>
                      <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}
              </CardContent>
            </Card>)}

            {/* Results for Sectors */}
            {results.length > 0 && generationType === 'sectors' && (
              <Card>
                <CardHeader>
                  <CardTitle>Generation Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : result.skipped ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="flex-1">{result.name}</span>
                        {result.imageUrl && (
                          <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            View Image
                          </a>
                        )}
                        {result.reason && (
                          <span className="text-xs text-muted-foreground">{result.reason}</span>
                        )}
                        {result.error && (
                          <span className="text-xs text-red-500">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {/*
                ⛔ KEYED ON THE TYPE, NOT A TWO-WAY TERNARY. This read
                   `generationType === 'personas' ? 'Persona' : 'Sector'`, which was correct while
                   there were exactly two kinds and silently labelled the THIRD one "Sector Images"
                   the moment products existed. A binary conditional is a set-membership assumption
                   that stops being true the first time the set grows, and it fails quietly — the
                   dialog still renders, it just lies about what the button is going to spend money on.
              */}
              {regenerateAll ? 'Regenerate All' : 'Generate Missing'} {GENERATION_LABELS[generationType ?? 'personas'].noun} Images
            </DialogTitle>
            <DialogDescription>
              {/*
                ⛔ THE DIALOG MUST NAME THE COHORT IT IS ABOUT TO BILL FOR. It said "ALL products"
                   even when the operator had scoped the run to specific IDs — on the one screen
                   whose entire purpose is confirming a spend, and precisely when the operator is
                   trying to verify they typed the right SKU. Saying "ALL" when the truth is "one"
                   trains people to ignore the confirmation, which is worse than not having one.
              */}
              {generationType === 'products' && scopedProductIds.length > 0
                ? `This will generate images for ${scopedProductIds.length} specified product(s): ${scopedProductIds.join(', ')}.`
                  + (regenerateAll ? ' Existing images for those products will be replaced.' : '')
                : regenerateAll
                  ? `This will regenerate ALL ${GENERATION_LABELS[generationType ?? 'personas'].plural} images, replacing existing ones. This process may take several minutes.`
                  : `This will generate images only for ${GENERATION_LABELS[generationType ?? 'personas'].missingOnly}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                Images will be generated using Leonardo.ai with photo-realistic quality and automatically uploaded to the CDN.
                {generationType === 'products' && scopedProductIds.length === 0 && (
                  <strong> This covers the whole site — every product without one of our images will be billed.</strong>
                )}
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmGeneration}>
              <Sparkles className="h-4 w-4 mr-2" />
              Start Generation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}